import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from 'lucide-react'
import { toast } from 'sonner'
import {
  addDays,
  dateFromString,
  formatDecimalHours,
  localDate,
  monthRange,
  startOfWeek,
  type ReportFormat
} from '@coneno/c-sheet-shared'
import { AppSelect } from '@/components/forms/app-select'
import { SegmentedControl } from '@/components/forms/segmented-control'
import { PageBody, PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useCustomers } from '@/hooks/use-customers'
import { usePeople } from '@/hooks/use-people'
import { useProjects } from '@/hooks/use-projects'
import { useAllTasks } from '@/hooks/use-tasks'
import { useTimeEntries } from '@/hooks/use-timesheets'
import { downloadText } from '@/lib/download'
import { buildReportExport } from '@/lib/report-export'
import {
  groupReport,
  minutesOverTime,
  reportTotals,
  splitByTask,
  type ReportGroup,
  type ReportGrouping
} from '@/lib/report-summary'
import { cn } from '@/lib/utils'
import { TaskSplitChart, type TaskHighlight } from './components/task-split-chart'
import { TimeBarChart } from './components/time-bar-chart'

type Period = 'week' | 'month' | 'last-month' | 'custom'

const isDate = (value: string | null): value is string =>
  Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value))

function hours(minutes: number): string {
  return `${formatDecimalHours(minutes)} h`
}

function periodRange(period: Exclude<Period, 'custom'>): { from: string; to: string } {
  if (period === 'week') {
    const from = startOfWeek(localDate())
    return { from, to: addDays(from, 6) }
  }
  return monthRange(localDate(), period === 'month' ? 0 : -1)
}

const entryDateFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
})

export function ReportPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultRange = periodRange('month')
  const fromDate = isDate(searchParams.get('from')) ? searchParams.get('from')! : defaultRange.from
  const toDate = isDate(searchParams.get('to')) ? searchParams.get('to')! : defaultRange.to
  const period = (searchParams.get('period') as Period | null) ?? 'month'
  const personId = searchParams.get('person') ?? ''
  const customerId = searchParams.get('client') ?? ''
  const projectId = searchParams.get('project') ?? ''
  const taskId = searchParams.get('task') ?? ''
  const billableFilter = searchParams.get('billable') ?? ''
  const grouping: ReportGrouping = searchParams.get('by') === 'person' ? 'person' : 'project'
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const dateRangeIsValid = fromDate <= toDate

  // Filters live in the address, so a report can be bookmarked or sent to a colleague.
  function update(changes: Record<string, string | null>): void {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setSearchParams(next, { replace: true })
  }

  function choosePeriod(next: Exclude<Period, 'custom'>): void {
    const range = periodRange(next)
    update({ period: next === 'month' ? null : next, from: range.from, to: range.to })
  }

  function changeMonth(offset: number): void {
    const range = monthRange(fromDate, offset)
    update({ period: 'custom', from: range.from, to: range.to })
  }

  const entries = useTimeEntries(
    dateRangeIsValid ? { personId: personId || undefined, from: fromDate, to: toDate } : {}
  )
  const people = usePeople()
  const projects = useProjects()
  const customers = useCustomers()
  const tasks = useAllTasks()

  const personNames = new Map(people.data?.map((person) => [person.id, person.name]))
  const projectsById = new Map(projects.data?.map((project) => [project.id, project]))
  const projectNames = new Map(projects.data?.map((project) => [project.id, project.name]))
  const projectCustomers = new Map(
    projects.data?.map((project) => [project.id, project.customerId])
  )
  const customerNames = new Map(customers.data?.map((customer) => [customer.id, customer.name]))
  const taskNames = new Map(tasks.data?.map((task) => [task.id, task.name]))

  const filteredEntries = dateRangeIsValid
    ? (entries.data ?? [])
        .filter(
          (entry) =>
            (!customerId || projectsById.get(entry.projectId)?.customerId === customerId) &&
            (!projectId || entry.projectId === projectId) &&
            (!taskId || entry.taskId === taskId) &&
            (!billableFilter || String(entry.billable) === billableFilter)
        )
        .sort(
          (left, right) =>
            left.workDate.localeCompare(right.workDate) ||
            left.startTime.localeCompare(right.startTime)
        )
    : []
  const totals = reportTotals(filteredEntries)
  const groups = groupReport(filteredEntries, grouping)
  const overTime = minutesOverTime(filteredEntries, fromDate, toDate)
  // The project and task hovered in the donut, highlighted in the breakdown.
  const [highlight, setHighlight] = useState<TaskHighlight | null>(null)
  const isLoading =
    entries.isLoading ||
    people.isLoading ||
    projects.isLoading ||
    customers.isLoading ||
    tasks.isLoading
  const error = entries.error ?? people.error ?? projects.error ?? customers.error ?? tasks.error
  const hasFilters = Boolean(personId || customerId || projectId || taskId || billableFilter)
  const reportProjects =
    projects.data?.filter((project) => !customerId || project.customerId === customerId) ?? []
  const reportTasks =
    tasks.data?.filter(
      (task) =>
        (!projectId || task.projectId === projectId) &&
        reportProjects.some((project) => project.id === task.projectId)
    ) ?? []

  function groupName(id: string, kind: ReportGrouping): string {
    if (kind === 'person') return personNames.get(id) ?? 'Unknown person'
    const project = projectsById.get(id)
    if (!project) return 'Unknown project'
    const client = project.customerId ? customerNames.get(project.customerId) : null
    return client ? `${project.name} · ${client}` : project.name
  }

  function rowName(group: ReportGroup): string {
    if (group.level !== 'task') return groupName(group.id, group.level)
    return group.id ? (taskNames.get(group.id) ?? 'Unknown task') : 'No task'
  }

  // Rows for one level of the breakdown and, where expanded, the levels underneath.
  function breakdownRows(
    nodes: ReportGroup[],
    path = '',
    depth = 0,
    projectId: string | null = null
  ): React.ReactNode[] {
    return nodes.flatMap((group) => {
      const key = `${path}/${group.level}:${group.id}`
      const open = expanded.has(key)
      const rowProject = group.level === 'project' ? group.id : projectId
      const highlighted =
        highlight !== null &&
        rowProject === highlight.projectId &&
        (group.level === 'project' ||
          (group.level === 'task' && group.id === (highlight.taskId ?? '')))
      const row = (
        <tr
          key={key}
          className={cn(
            'border-b transition-colors',
            depth > 0 && 'bg-muted/30',
            depth > 1 && 'text-muted-foreground',
            highlighted && 'bg-primary/10 text-foreground'
          )}
        >
          <th
            className={cn(
              'py-1.5 pr-3 text-left',
              depth === 0 ? 'py-2 font-medium' : 'font-normal'
            )}
            style={{ paddingLeft: `${0.75 + depth * 1.5}rem` }}
          >
            {group.children.length ? (
              <button
                type="button"
                aria-expanded={open}
                onClick={() => toggle(key)}
                className="flex items-center gap-1.5 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronDownIcon
                  aria-hidden="true"
                  className={cn(
                    'size-4 text-muted-foreground transition-transform',
                    !open && '-rotate-90'
                  )}
                />
                {rowName(group)}
              </button>
            ) : (
              <span className="pl-5.5">{rowName(group)}</span>
            )}
          </th>
          <td
            className={cn(
              'px-3 text-right whitespace-nowrap tabular-nums',
              depth === 0 ? 'py-2 font-medium' : 'py-1.5'
            )}
          >
            {hours(group.minutes)}
          </td>
          <td className="px-3 text-right whitespace-nowrap tabular-nums">
            {hours(group.billableMinutes)}
          </td>
          <td className="px-3 text-right whitespace-nowrap tabular-nums">
            {totals.minutes ? Math.round((group.minutes / totals.minutes) * 100) : 0}%
          </td>
        </tr>
      )
      return open ? [row, ...breakdownRows(group.children, key, depth + 1, rowProject)] : [row]
    })
  }

  function exportReport(format: ReportFormat): void {
    if (!filteredEntries.length) return
    const content = buildReportExport(
      filteredEntries,
      { personNames, projectNames, projectCustomers, customerNames, taskNames },
      format
    )
    downloadText(
      `c-sheet-report-${fromDate}-to-${toDate}.${format}`,
      content,
      format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json'
    )
    toast(`Report downloaded as ${format.toUpperCase()}`)
  }

  function toggle(id: string): void {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const filterTriggerClass = 'h-7 w-auto max-w-56 text-xs'

  return (
    <PageBody>
      <PageHeader title="Reports">
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            ariaLabel="Period"
            value={period}
            onChange={choosePeriod}
            options={[
              { value: 'week', label: 'This week' },
              { value: 'month', label: 'This month' },
              { value: 'last-month', label: 'Last month' }
            ]}
          />
          <div className="flex items-center gap-1">
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="Previous month"
              onClick={() => changeMonth(-1)}
            >
              <ChevronLeftIcon />
            </Button>
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="Next month"
              onClick={() => changeMonth(1)}
            >
              <ChevronRightIcon />
            </Button>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <Input
              type="date"
              aria-label="From"
              className="h-8 w-auto"
              value={fromDate}
              aria-invalid={!dateRangeIsValid}
              onChange={(event) =>
                isDate(event.target.value) && update({ period: 'custom', from: event.target.value })
              }
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              aria-label="To"
              className="h-8 w-auto"
              value={toDate}
              aria-invalid={!dateRangeIsValid}
              onChange={(event) =>
                isDate(event.target.value) && update({ period: 'custom', to: event.target.value })
              }
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  className="ml-auto h-9 w-36"
                  disabled={isLoading || !filteredEntries.length}
                />
              }
            >
              <DownloadIcon data-icon="inline-start" />
              Export
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => exportReport('csv')}>
                  CSV
                  <span className="text-muted-foreground">for spreadsheets</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportReport('json')}>
                  JSON
                  <span className="text-muted-foreground">structured data</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AppSelect
            ariaLabel="Person"
            size="sm"
            triggerClassName={filterTriggerClass}
            value={personId}
            onValueChange={(value) => update({ person: value })}
            options={[
              { value: '', label: 'All people' },
              ...(people.data?.map((person) => ({ value: person.id, label: person.name })) ?? [])
            ]}
          />
          <AppSelect
            ariaLabel="Client"
            size="sm"
            triggerClassName={filterTriggerClass}
            value={customerId}
            onValueChange={(value) => update({ client: value, project: null, task: null })}
            options={[
              { value: '', label: 'All clients' },
              ...(customers.data?.map((customer) => ({
                value: customer.id,
                label: customer.name
              })) ?? [])
            ]}
          />
          <AppSelect
            ariaLabel="Project"
            size="sm"
            triggerClassName={filterTriggerClass}
            value={projectId}
            onValueChange={(value) => update({ project: value, task: null })}
            options={[
              { value: '', label: 'All projects' },
              ...reportProjects.map((project) => ({ value: project.id, label: project.name }))
            ]}
          />
          <AppSelect
            ariaLabel="Task"
            size="sm"
            triggerClassName={filterTriggerClass}
            value={taskId}
            onValueChange={(value) => update({ task: value })}
            options={[
              { value: '', label: 'All tasks' },
              ...reportTasks.map((task) => ({ value: task.id, label: task.name }))
            ]}
          />
          <AppSelect
            ariaLabel="Billable"
            size="sm"
            triggerClassName={filterTriggerClass}
            value={billableFilter}
            onValueChange={(value) => update({ billable: value })}
            options={[
              { value: '', label: 'Billable and non-billable' },
              { value: 'true', label: 'Billable only' },
              { value: 'false', label: 'Non-billable only' }
            ]}
          />
          {hasFilters && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                update({ person: null, client: null, project: null, task: null, billable: null })
              }
            >
              Clear filters
            </Button>
          )}
        </div>
      </PageHeader>

      {!dateRangeIsValid && (
        <p role="alert" className="text-sm text-destructive">
          The start date must be on or before the end date.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          Could not load the report: {error.message}
        </p>
      )}

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <section
            aria-labelledby="report-over-time"
            className="flex flex-col gap-3 rounded-lg border bg-card p-4 lg:col-span-2"
          >
            <h2 id="report-over-time" className="text-xs font-medium text-muted-foreground">
              Hours per {overTime.unit}
            </h2>
            <TimeBarChart {...overTime} />
          </section>
          <section
            aria-labelledby="report-split"
            className="flex flex-col gap-3 rounded-lg border bg-card p-4"
          >
            <h2 id="report-split" className="text-xs font-medium text-muted-foreground">
              By project and task
            </h2>
            <div className="flex flex-1 items-center justify-center">
              <TaskSplitChart
                slices={splitByTask(filteredEntries)}
                projectName={(id) => projectNames.get(id) ?? 'Unknown project'}
                taskName={(id) => taskNames.get(id) ?? 'Unknown task'}
                totalMinutes={totals.minutes}
                onHighlight={setHighlight}
              />
            </div>
          </section>
        </div>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="report-breakdown">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="report-breakdown" className="text-base font-semibold">
            Breakdown
          </h2>
          <SegmentedControl
            ariaLabel="Group by"
            size="xs"
            value={grouping}
            onChange={(value) => update({ by: value === 'project' ? null : value })}
            options={[
              { value: 'project', label: 'By project' },
              { value: 'person', label: 'By person' }
            ]}
          />
        </div>
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-140 text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">
                  {grouping === 'project' ? 'Project / task / person' : 'Person / project / task'}
                </th>
                <th className="px-3 py-2 text-right font-medium">Hours</th>
                <th className="px-3 py-2 text-right font-medium">Billable</th>
                <th className="px-3 py-2 text-right font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="p-3">
                    <Skeleton className="h-16 w-full" />
                  </td>
                </tr>
              ) : groups.length ? (
                breakdownRows(groups)
              ) : (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                    No time recorded for this selection.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="report-entries">
        <h2 id="report-entries" className="text-base font-semibold">
          Entries{' '}
          <span className="font-normal text-muted-foreground">({filteredEntries.length})</span>
        </h2>
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-180 text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">Person</th>
                <th className="px-3 py-2 text-left font-medium">Project</th>
                <th className="px-3 py-2 text-left font-medium">Time</th>
                <th className="px-3 py-2 text-right font-medium">Hours</th>
                <th className="px-3 py-2 text-left font-medium">Description</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr key={entry.id} className="border-b last:border-b-0">
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <Link
                      className="underline-offset-2 hover:underline"
                      to={`/project-sheet?week=${startOfWeek(entry.workDate)}`}
                    >
                      {entryDateFormat.format(dateFromString(entry.workDate))}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {personNames.get(entry.personId) ?? 'Unknown person'}
                  </td>
                  <td className="px-3 py-1.5">
                    {projectNames.get(entry.projectId) ?? 'Unknown project'}
                    {entry.taskId && (
                      <span className="text-muted-foreground">
                        {' '}
                        · {taskNames.get(entry.taskId) ?? 'Unknown task'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">
                    {entry.startTime}–{entry.endTime}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatDecimalHours(entry.durationMinutes)}
                    {!entry.billable && (
                      <span className="ml-1 text-xs text-muted-foreground" title="Non-billable">
                        nb
                      </span>
                    )}
                  </td>
                  <td
                    className="max-w-80 truncate px-3 py-1.5 text-muted-foreground"
                    title={entry.description ?? undefined}
                  >
                    {entry.description}
                  </td>
                </tr>
              ))}
              {!isLoading && !filteredEntries.length && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    No entries for this selection.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </PageBody>
  )
}
